import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type { ActionCardSummary, ControlRoomPacket } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "@/components/ui/states";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

import {
  Field,
  FieldGrid,
  InspectorSection,
  RefLink,
} from "@/components/inspector";
import { ActionCardLifecycleActions } from "@/resources/action-cards/ActionCardLifecycleActions";
import { formatScore } from "@/resources/action-cards/model";

interface ControlRoomPanelProps {
  contextId: string;
  fullPage?: boolean;
}

function useControlRoomPacket(contextId: string) {
  const [data, setData] = React.useState<ControlRoomPacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ControlRoomPacket>(
        `/contexts/${encodeURIComponent(contextId)}/control-room`,
      )
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load Control Room.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contextId, reloadKey]);

  return { data, loading, error, reload };
}

function SummaryMetric({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-sm tabular-nums text-foreground">
        {value}
      </p>
    </div>
  );
}

function ActionCardRailItem({
  card,
  onChanged,
}: {
  card: ActionCardSummary;
  onChanged: () => void;
}) {
  const go = useGo();
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={card.status} />
            <Badge variant="secondary">{humanize(card.severity)}</Badge>
            <Badge variant="outline">{humanize(card.risk_level)}</Badge>
          </div>
          <p className="mt-2 text-sm font-medium [overflow-wrap:anywhere]">
            {card.title}
          </p>
          {card.summary ? (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {card.summary}
            </p>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go({ to: `/action-cards/${card.id}` })}
        >
          Open
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-4">
        <span>{humanize(card.subject_type)}</span>
        <span>Support {card.support_count}</span>
        <span>Confidence {formatScore(card.confidence)}</span>
        <span>Seen {formatDateTime(card.last_seen_at)}</span>
      </div>
      <div className="mt-3">
        <ActionCardLifecycleActions card={card} onChanged={onChanged} />
      </div>
    </div>
  );
}

function ControlRoomContent({
  packet,
  reload,
  fullPage,
}: {
  packet: ControlRoomPacket;
  reload: () => void;
  fullPage?: boolean;
}) {
  const stream = packet.recommendation_stream;
  const profile = packet.monitoring_profile;
  const cards = stream.cards;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <SummaryMetric label="Cards" value={stream.summary.total} />
        <SummaryMetric label="Proposed" value={stream.summary.proposed} />
        <SummaryMetric label="Accepted" value={stream.summary.accepted} />
        <SummaryMetric label="Deferred" value={stream.summary.deferred} />
        <SummaryMetric label="Failed" value={stream.summary.failed} />
        <SummaryMetric
          label="Redacted"
          value={stream.summary.redacted_support_refs}
        />
      </div>

      <InspectorSection
        title={fullPage ? "Operating Profile" : undefined}
        aside={
          <span className="text-xs text-muted-foreground">
            Generated {formatDateTime(packet.generated_at)}
          </span>
        }
      >
        <FieldGrid>
          <Field label="Context">
            <RefLink
              resource="contexts"
              id={packet.context.id}
              label={packet.context.name}
            />
          </Field>
          <Field label="Activation">
            <Badge variant="outline">{humanize(packet.activation_state)}</Badge>
          </Field>
          <Field label="Maturity">
            {humanize(packet.context.maturity_stage)}
          </Field>
          <Field label="Surface state">
            {humanize(stream.surface_state)}
          </Field>
          <Field label="Monitoring">
            {profile ? humanize(profile.profile_kind) : "Setup required"}
          </Field>
          <Field label="Action mode">
            {profile ? humanize(profile.action_mode) : "-"}
          </Field>
          <Field label="Cadence">
            {profile ? humanize(profile.cadence) : "-"}
          </Field>
          <Field label="Next review">
            {profile ? formatDateTime(profile.next_review_at) : "-"}
          </Field>
          <Field label="Lens">
            {packet.control_view.lens?.name ?? "-"}
          </Field>
          <Field label="Saved view">
            {packet.control_view.saved_view?.name ?? "-"}
          </Field>
        </FieldGrid>
      </InspectorSection>

      {packet.warnings.length > 0 ? (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
            <Icon name="controlRoom" size={16} aria-hidden />
            Setup notes
          </div>
          <ul className="list-inside list-disc">
            {packet.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <InspectorSection
        title="Recommendation Stream"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" size={16} className="mr-2" aria-hidden />
            Refresh
          </Button>
        }
      >
        {cards.length === 0 ? (
          <EmptyState
            icon="actionCards"
            title="No action cards in this Control Room."
            hint="Diagnostics, rules, and agents will write proposals here when there is a concrete next action."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {cards.map((card) => (
              <ActionCardRailItem
                key={card.id}
                card={card}
                onChanged={reload}
              />
            ))}
          </div>
        )}
      </InspectorSection>
    </div>
  );
}

export function ControlRoomPanel({
  contextId,
  fullPage,
}: ControlRoomPanelProps) {
  const { data, loading, error, reload } = useControlRoomPacket(contextId);

  if (loading) return <LoadingState rows={5} />;
  if (error || !data) {
    return (
      <ErrorState
        message={error ?? "Control Room packet was not returned."}
        onRetry={reload}
      />
    );
  }

  return (
    <ControlRoomContent packet={data} reload={reload} fullPage={fullPage} />
  );
}

export function ContextControlRoomRoute() {
  const { id } = useParams<{ id: string }>();
  const go = useGo();

  if (!id) {
    return <ErrorState message="Missing context id." />;
  }

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Control Room"
          subtitle="Governed context surface: monitoring profile, view configuration, warnings, and recommendation stream."
          icon="controlRoom"
          actions={
            <Button variant="outline" onClick={() => go({ to: `/contexts/${id}` })}>
              <Icon name="contexts" size={16} className="mr-2" aria-hidden />
              Context
            </Button>
          }
        />
        <ControlRoomPanel contextId={id} fullPage />
      </div>
    </div>
  );
}
